/**
 * Auth Slice
 * Manages authentication state
 */

import { createSlice, createAsyncThunk, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { api, TokenManager } from '@/shared/api';
import { API_ROUTES } from '@/config';
import type { RootState } from '../index';
import type { User } from '@/types/entities';
import { apiErrorDetail } from '@/utils/errorHandler';

// ===========================================
// Types
// ===========================================

// Re-export User type for consumers of this slice
export type { User } from '@/types/entities';

export interface AuthState {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    isInitialized: boolean; // True after initial auth check completes
    error: string | null;
}

// ===========================================
// Initial State
// ===========================================

const initialState: AuthState = {
    user: null,
    isAuthenticated: false,
    isLoading: false,
    isInitialized: false, // Will be set to true after first auth check
    error: null,
};

// ===========================================
// Async Thunks
// ===========================================

interface LoginResponse {
    access_token: string;
    token_type: string;
    refresh_token?: string;
}

export const login = createAsyncThunk(
    'auth/login',
    async (credentials: { email: string; password: string }, { rejectWithValue }) => {
        try {
            // Login and get token
            const response = await api.post<LoginResponse>(
                API_ROUTES.auth.login,
                credentials
            );

            TokenManager.setToken(response.access_token);
            if (response.refresh_token) {
                TokenManager.setRefreshToken(response.refresh_token);
            }

            // Fetch user data with the new token
            const userResponse = await api.get<User>(API_ROUTES.auth.me);
            return userResponse;
        } catch (error: unknown) {
            // A string even when the detail is coded (`{code, message}`, e.g. a blocked country).
            return rejectWithValue(apiErrorDetail(error, (error as Error)?.message || 'Login failed'));
        }
    }
);

export const phoneLogin = createAsyncThunk(
    'auth/phoneLogin',
    async (credentials: { phone: string; code: string }, { rejectWithValue }) => {
        try {
            const response = await api.post<LoginResponse>(
                API_ROUTES.auth.phoneLogin,
                credentials
            );

            TokenManager.setToken(response.access_token);
            if (response.refresh_token) {
                TokenManager.setRefreshToken(response.refresh_token);
            }

            const userResponse = await api.get<User>(API_ROUTES.auth.me);
            return userResponse;
        } catch (error: unknown) {
            // A string even when the detail is coded (`{code, message}`, e.g. a blocked country).
            return rejectWithValue(apiErrorDetail(error, (error as Error)?.message || 'Sign in failed'));
        }
    }
);

export const logout = createAsyncThunk(
    'auth/logout',
    async () => {
        // Sessions are bearer tokens with no server-side state, so signing
        // out means forgetting them here. (The old POST /auth/logout had no
        // backend route and 404ed on every sign-out.)
        TokenManager.clearTokens();
    }
);

export const getCurrentUser = createAsyncThunk(
    'auth/getCurrentUser',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get<User>(API_ROUTES.auth.me);
            return response;
        } catch (error: unknown) {
            TokenManager.clearTokens();
            const err = error as { message?: string; detail?: string };
            return rejectWithValue(err.detail || err.message || 'Failed to get user');
        }
    }
);

// ===========================================
// Slice
// ===========================================

export const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setUser: (state, action: PayloadAction<User | null>) => {
            state.user = action.payload;
            state.isAuthenticated = !!action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
        resetAuth: () => initialState,
        skipInitialization: (state) => {
            // Called when no token exists - mark as initialized without fetching user
            state.isInitialized = true;
            state.isLoading = false;
        },
    },
    extraReducers: (builder) => {
        // Login
        builder
            .addCase(login.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(login.fulfilled, (state, action) => {
                state.isLoading = false;
                state.user = action.payload;
                state.isAuthenticated = true;
            })
            .addCase(login.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });

        // Phone Login
        builder
            .addCase(phoneLogin.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(phoneLogin.fulfilled, (state, action) => {
                state.isLoading = false;
                state.user = action.payload;
                state.isAuthenticated = true;
            })
            .addCase(phoneLogin.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });

        // Logout
        builder
            .addCase(logout.fulfilled, (state) => {
                state.user = null;
                state.isAuthenticated = false;
                state.error = null;
            });

        // Get Current User
        builder
            .addCase(getCurrentUser.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getCurrentUser.fulfilled, (state, action) => {
                state.isLoading = false;
                state.user = action.payload;
                state.isAuthenticated = true;
                state.isInitialized = true;
            })
            .addCase(getCurrentUser.rejected, (state) => {
                state.isLoading = false;
                state.user = null;
                state.isAuthenticated = false;
                state.isInitialized = true;
            });
    },
});

// Actions
export const { setUser, clearError, resetAuth, skipInitialization } = authSlice.actions;

// ===========================================
// Selectors
// ===========================================

// Basic selectors (primitive values - no memoization needed)
export const selectUser = (state: RootState) => state.auth.user;
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectAuthLoading = (state: RootState) => state.auth.isLoading;
export const selectAuthInitialized = (state: RootState) => state.auth.isInitialized;
export const selectAuthError = (state: RootState) => state.auth.error;

// Memoized derived selectors
export const selectIsAdmin = createSelector(
    [selectUser],
    (user) => user?.role === 'admin'
);

export const selectUserRole = createSelector(
    [selectUser],
    (user) => user?.role ?? null
);

export const selectAuthStatus = createSelector(
    [selectIsAuthenticated, selectAuthLoading, selectAuthInitialized],
    (isAuthenticated, isLoading, isInitialized) => ({
        isAuthenticated,
        isLoading,
        isInitialized,
        isReady: isInitialized && !isLoading,
    })
);

export default authSlice.reducer;
